import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import { MoreVertical } from 'lucide-react';

interface ActionItem {
  label: string;
  onClick?: () => void;
  to?: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'outline' | 'ghost';
  disabled?: boolean;
  icon?: React.ReactNode;
}

interface ActionMenuProps {
  actions: ActionItem[];
  className?: string;
}

const ActionMenu: React.FC<ActionMenuProps> = ({ actions, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      {/* Desktop: Show all buttons */}
      <div className="hidden sm:flex flex-col sm:flex-row gap-2">
        {actions.map((action, index) => {
          const handleClick = () => {
            if (action.to) {
              navigate(action.to);
            } else if (action.onClick) {
              action.onClick();
            }
          };
          
          return (
            <Button
              key={index}
              onClick={handleClick}
              variant={action.variant || 'primary'}
              size="md"
              disabled={action.disabled}
              fullWidth
              className="sm:w-auto"
            >
              {action.icon && <span className="mr-2">{action.icon}</span>}
              {action.label}
            </Button>
          );
        })}
      </div>

      {/* Mobile: Show ellipsis menu */}
      <div className="sm:hidden">
        <Button
          onClick={() => setIsOpen(!isOpen)}
          variant="outline"
          size="md"
          aria-label="Actions"
        >
          <MoreVertical className="w-4 h-4" />
        </Button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
            <div className="py-1">
              {actions.map((action, index) => {
                const handleClick = () => {
                  if (action.to) {
                    navigate(action.to);
                  } else if (action.onClick) {
                    action.onClick();
                  }
                  setIsOpen(false);
                };
                
                return (
                  <button
                    key={index}
                    onClick={handleClick}
                    disabled={action.disabled}
                    className={`w-full px-4 py-3 text-left text-sm min-h-[44px] flex items-center gap-2 ${
                      action.disabled
                        ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : action.variant === 'danger'
                        ? 'text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {action.icon && <span>{action.icon}</span>}
                    {action.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionMenu;

